import React, { useCallback, useEffect, useState } from 'react';
import PageHeader from './components/PageHeader';
import { apiFetch, apiUrl } from './utils/api';

const API_BASE = apiUrl('/api/marketing/images');

function SettingsMarketingImageGeneration() {
  const [moduleSettings, setModuleSettings] = useState({
    marketing_hub: true,
    marketing_image_generation: false,
  });
  const [providerOptions, setProviderOptions] = useState([]);
  const [providerSettings, setProviderSettings] = useState({
    provider: 'off',
    model: '',
    enabled: false,
    has_api_key: false,
    api_key_mask: '',
  });
  const [providerConfigs, setProviderConfigs] = useState({});
  const [fallbackOrder, setFallbackOrder] = useState([]);
  const [providerKey, setProviderKey] = useState('');
  const [testPrompt, setTestPrompt] = useState('automated sliding security gate at a UK business park entrance');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const companyRes = await apiFetch('/api/company-settings');
      const companySettings = await companyRes.json();
      if (companyRes.ok) {
        setModuleSettings({
          marketing_hub: companySettings.module_marketing_hub !== false,
          marketing_image_generation: companySettings.module_marketing_image_generation === true,
        });
      }

      const res = await apiFetch(`${API_BASE}/generation/settings`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to load image generation settings');
      setProviderOptions(data.providers || []);
      setProviderSettings(data.settings || {});
      setProviderConfigs(data.settings?.provider_configs || {});
      setFallbackOrder(data.settings?.fallback_order || []);
    } catch (err) {
      setError(err.message || 'Unable to load image generation settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const selectedProvider = providerOptions.find(provider => provider.id === providerSettings.provider);
  const selectedProviderConfig = providerConfigs[providerSettings.provider] || {};
  const generationProviders = providerOptions.filter(provider => provider.id !== 'off');

  const updateFallbackProvider = (providerId, checked) => {
    setFallbackOrder(prev => {
      const without = prev.filter(item => item !== providerId);
      return checked ? [...without, providerId] : without;
    });
  };

  const saveSettings = async ({ silent = false } = {}) => {
    setSaving(true);
    setError('');
    if (!silent) setMessage('');
    try {
      const payload = {
        provider: providerSettings.provider,
        model: providerSettings.model,
        enabled: providerSettings.provider !== 'off' && providerSettings.enabled !== false,
        fallback_order: fallbackOrder,
      };
      if (providerKey.trim()) payload.api_key = providerKey.trim();

      const res = await apiFetch(`${API_BASE}/generation/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to save provider settings');
      setProviderSettings(data.settings || {});
      setProviderConfigs(data.settings?.provider_configs || {});
      setFallbackOrder(data.settings?.fallback_order || []);
      setProviderKey('');
      if (!silent) setMessage('Image generation setup saved.');
      return data.settings || {};
    } catch (err) {
      setError(err.message || 'Unable to save provider settings');
      if (silent) throw err;
    } finally {
      setSaving(false);
    }
  };

  const generateTestImage = async () => {
    setGenerating(true);
    setError('');
    setMessage('');
    try {
      if ((providerSettings.provider || 'off') === 'off') {
        throw new Error('Choose an image provider before generating an image.');
      }
      if (!providerKey.trim() && !selectedProviderConfig.has_api_key && !providerSettings.uses_env_key) {
        throw new Error('Paste an API token before generating an image.');
      }
      await saveSettings({ silent: true });
      const res = await apiFetch(`${API_BASE}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: testPrompt,
          topic: 'Marketing image generator test',
          audience: 'commercial',
          label: 'Generated image test',
          category: 'newsletter',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.details || data.error || 'Unable to generate image');
      setMessage(`Test image generated with ${data.provider || 'selected provider'} and added to the marketing image library.`);
    } catch (err) {
      setError(err.message || 'Unable to generate image');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Marketing Image Generation"
        subtitle="Configure generated image providers for newsletters and blogs"
        backTo="/settings/operations-files"
        backLabel="Operations Setup"
        sticky
      />

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">{error}</div>}
        {message && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">{message}</div>}

        {!moduleSettings.marketing_image_generation && (
          <section className="bg-amber-50 border border-amber-200 rounded-lg p-5">
            <h2 className="text-lg font-semibold text-amber-900">Module Disabled</h2>
            <p className="text-sm text-amber-800 mt-1">
              Generated Marketing Images must be enabled in Management Hub before providers can generate images.
            </p>
          </section>
        )}

        <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Provider Setup</h2>
              <p className="text-sm text-gray-500 mt-1">
                Save credentials for more than one provider, choose the primary provider, and tick fallback providers.
              </p>
            </div>

            {loading ? (
              <p className="text-sm text-gray-500">Loading image generation setup...</p>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className="space-y-1">
                    <span className="text-xs font-semibold text-gray-600 uppercase">Primary provider</span>
                    <select
                      value={providerSettings.provider || 'off'}
                      onChange={event => {
                        const next = providerOptions.find(provider => provider.id === event.target.value);
                        const saved = providerConfigs[event.target.value] || {};
                        setProviderSettings(prev => ({
                          ...prev,
                          provider: event.target.value,
                          model: saved.model || next?.defaultModel || '',
                          enabled: event.target.value !== 'off' && saved.enabled !== false,
                          has_api_key: saved.has_api_key || false,
                          api_key_mask: saved.api_key_mask || '',
                        }));
                        setProviderKey('');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                    >
                      {providerOptions.map(provider => (
                        <option key={provider.id} value={provider.id}>{provider.name}</option>
                      ))}
                    </select>
                  </label>

                  <label className="space-y-1">
                    <span className="text-xs font-semibold text-gray-600 uppercase">Model</span>
                    <input
                      value={providerSettings.model || ''}
                      onChange={event => setProviderSettings(prev => ({ ...prev, model: event.target.value }))}
                      disabled={(providerSettings.provider || 'off') === 'off'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-100"
                    />
                  </label>

                  <label className="space-y-1">
                    <span className="text-xs font-semibold text-gray-600 uppercase">API token</span>
                    <input
                      type="password"
                      value={providerKey}
                      onChange={event => setProviderKey(event.target.value)}
                      disabled={(providerSettings.provider || 'off') === 'off'}
                      placeholder={selectedProviderConfig.has_api_key ? selectedProviderConfig.api_key_mask || 'Already configured' : 'Paste token'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-100"
                    />
                  </label>
                </div>

                <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                  <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Fallback providers</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {generationProviders.map(provider => {
                      const config = providerConfigs[provider.id] || {};
                      const checked = fallbackOrder.includes(provider.id) || providerSettings.provider === provider.id;
                      return (
                        <label key={provider.id} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={providerSettings.provider === provider.id}
                            onChange={event => updateFallbackProvider(provider.id, event.target.checked)}
                            className="rounded border-gray-300 text-[#172378]"
                          />
                          <span>{provider.name}</span>
                          <span className={`text-xs ${config.has_api_key ? 'text-green-700' : 'text-gray-400'}`}>
                            {config.has_api_key ? 'saved' : 'no key'}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={(providerSettings.provider || 'off') !== 'off' && providerSettings.enabled !== false}
                      onChange={event => setProviderSettings(prev => ({ ...prev, enabled: event.target.checked }))}
                      disabled={(providerSettings.provider || 'off') === 'off'}
                      className="rounded border-gray-300 text-[#172378]"
                    />
                    Enable automatic generated images
                  </label>
                  {selectedProvider && <span className="text-xs text-gray-500">{selectedProvider.description}</span>}
                </div>

                <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
                  <label className="flex-1 space-y-1">
                    <span className="text-xs font-semibold text-gray-600 uppercase">Test prompt</span>
                    <textarea
                      value={testPrompt}
                      onChange={event => setTestPrompt(event.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2 lg:w-80">
                    <button
                      type="button"
                      onClick={saveSettings}
                      disabled={saving}
                      className="flex-1 px-4 py-2 rounded-md bg-[#172378] text-white text-sm font-semibold hover:bg-[#0f185b] disabled:opacity-60"
                    >
                      {saving ? 'Saving...' : 'Save Setup'}
                    </button>
                    <button
                      type="button"
                      onClick={generateTestImage}
                      disabled={generating || saving || !moduleSettings.marketing_image_generation || (providerSettings.provider || 'off') === 'off'}
                      className="flex-1 px-4 py-2 rounded-md bg-gray-100 text-gray-800 text-sm font-semibold hover:bg-gray-200 disabled:opacity-60"
                    >
                      {generating ? 'Generating...' : 'Generate Image'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

export default SettingsMarketingImageGeneration;
